import { useLayoutEffect } from 'react';
import { APPEARANCE_LABELS, type AppearancePreference } from '@traceio-org/auth-ui';
import { hubUrl } from '@traceio-org/platform-contracts';
import { NavigationGlyph, type NavigationLink, type NavigationShellProps } from '@traceio-org/ui';
import { useAdmin, useStaff } from '../context';
import { STAFF_ROLE_LABELS, initialsOf } from '../format';
import { ADMIN_BRAND } from '../navigation';

/**
 * Trace Admin's brand.themeClassName. NavigationShell puts it on the whole shell and on every one of its popups (the
 * account menu, the phone slide-out, search, group flyouts), so a rule scoped by it alone reaches all of them: admin.css
 * anchors the account menu's adjustments on trace-ui's workspace-menu classes as well.
 */
export const ADMIN_NAV_CLASS = 'admin-nav';

/** The ids of the account menu's entries that leave Trace Admin for the account hub. */
export const ACCOUNT_BILLING_ID = 'account-billing';
export const SIGN_OUT_ID = 'sign-out';

/** The shell's footer row: a button only while the account menu is on, so it is found by its element. */
export const ACCOUNT_TRIGGER_SELECTOR = 'button.trace-workspace';

/** The account menu's lone workspace choice (Trace Admin itself), hidden and disabled. */
const WORKSPACE_OPTION_SELECTOR = `.${ADMIN_NAV_CLASS} button.trace-workspace-option`;

/** The Appearance entry, which opens the appearance menu; its href only matters for a modified click (a new tab). */
export const APPEARANCE_ITEM_ID = 'appearance';

/** "Appearance" in the account menu, with the current choice as its trailing text. */
function appearanceAction(preference: AppearancePreference): NavigationLink {
  return {
    id: APPEARANCE_ITEM_ID,
    label: 'Appearance',
    href: '#appearance',
    icon: <NavigationGlyph name="Sun" />,
    trailingLabel: APPEARANCE_LABELS[preference],
  };
}

/** Account & billing's glyph: lucide's circle-user-round (ISC), drawn like the navigation's glyphs. */
function AccountGlyph() {
  return (
    <svg
      className="trace-icon-native lucide icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 20a6 6 0 0 0-12 0" />
      <circle cx="12" cy="10" r="4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

/** Sign out's glyph: lucide's log-out (ISC). */
function SignOutGlyph() {
  return (
    <svg
      className="trace-icon-native lucide icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    </svg>
  );
}

type AccountMenuProps = Pick<
  NavigationShellProps,
  'brand' | 'workspaces' | 'activeWorkspaceId' | 'onWorkspaceChange' | 'user' | 'workspaceMenu'
>;

/**
 * Who is signed in, as NavigationShell's sidebar footer and its popup: the footer row shows the staff member's
 * initials and name, and opens the account menu (trace-ui's workspace menu: anchored to the row, above it, or beside
 * the collapsed rail; Escape, a click outside or choosing an entry closes it and gives focus back to the row). The
 * menu shows the name and staff role from GET /v1/admin/me, then Account & billing (the account hub's settings),
 * Appearance (the appearance menu) and Sign out (the hub's /logout?product=admin).
 *
 * trace-ui (vendored, e5ce5602ae) makes the footer row a button only when it has workspaces to switch between and
 * has no other way to open that popup, so Trace Admin passes itself as the one workspace. admin.css hides that lone
 * workspace choice and its "Account" heading (which still names the popup), and useAccountMarkup disables the choice
 * and names the row for what it opens. Remove all three once trace-ui opens the footer menu for actions alone (an
 * account slot, or a workspace menu without workspaces).
 */
export function useAccountMenu(preference: AppearancePreference): AccountMenuProps {
  const { person, context } = useStaff();
  const { hubOrigin } = useAdmin();
  const role = STAFF_ROLE_LABELS[context.staffRole];
  const initials = initialsOf(person.displayName);
  useAccountMarkup(person.displayName, `Account, ${person.displayName}, ${role}`);
  const actions: NavigationLink[] = [
    {
      id: ACCOUNT_BILLING_ID,
      label: 'Account & billing',
      // The account hub's own pages for the staff member's organization (members, billing); the hub signs them in
      // silently when the Auth0 session already exists. hubUrl has no settings route, so the path is joined here.
      href: new URL('/settings', hubOrigin).toString(),
      icon: <AccountGlyph />,
    },
    appearanceAction(preference),
    {
      id: SIGN_OUT_ID,
      label: 'Sign out',
      href: hubUrl(hubOrigin, { route: 'logout', productKey: 'admin' }),
      icon: <SignOutGlyph />,
    },
  ];
  const brand = {
    ...ADMIN_BRAND,
    workspaceName: person.displayName,
    initial: initials,
    themeClassName: ADMIN_NAV_CLASS,
  };
  return {
    brand,
    workspaces: [{ id: 'admin', brand }],
    activeWorkspaceId: 'admin',
    onWorkspaceChange: () => {},
    user: { name: person.displayName, initials },
    workspaceMenu: { label: 'Account', description: role, actions },
  };
}

/**
 * Adjusts trace-ui's markup where it has no prop: names the footer row for the menu it opens (trace-ui calls it
 * "Switch workspace, <name>"), and disables the lone workspace choice admin.css hides, since the menu's arrow keys
 * (trace-ui's focusSibling) move through every enabled button, hidden or not, and would stop on it. Applies to every
 * copy (the rail's row, the phone slide-out's, the popup, each mounted later at the end of <body>). It renames only a
 * row that still carries trace-ui's own words for this person, so it writes each row once (and again only if the shell
 * re-renders them), never fights another copy of the shell, and the observer never loops. A layout effect, so the
 * first paint already has the name. Remove with the lone workspace (see useAccountMenu); test/account-menu.test.tsx
 * fails when trace-ui's markup moves.
 */
function useAccountMarkup(name: string, label: string): void {
  useLayoutEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const vendored = `Switch workspace, ${name}`;
    const apply = () => {
      for (const row of document.querySelectorAll<HTMLElement>(ACCOUNT_TRIGGER_SELECTOR)) {
        if (row.getAttribute('aria-label') === vendored) row.setAttribute('aria-label', label);
      }
      for (const option of document.querySelectorAll<HTMLButtonElement>(WORKSPACE_OPTION_SELECTOR)) {
        if (!option.disabled) option.disabled = true;
      }
    };
    apply();
    // Rows and popups arrive as new nodes (the slide-out, each opening of the menu); React writes the row's label again
    // only when the name changes, which re-runs this effect. So new nodes are all it watches, never attributes.
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { subtree: true, childList: true });
    return () => observer.disconnect();
  }, [name, label]);
}
